import { useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useWorkbookDownload } from '../useWorkbookDownload';

interface ImportExportMenuProps {
  countries: Array<{ code: string; name: string }>;
  /** Pre-selected when the directory is filtered on exactly one country (M15). */
  defaultCountry?: string;
  canManage: boolean;
  /** Opens the import modal for the chosen country. */
  onImport: (countryCode: string) => void;
  /** Lets the page hand focus back to this button when the import modal closes (CA22). */
  triggerRef: RefObject<HTMLButtonElement>;
}

/** F31 — the country first, then the template and import (manage) and the base export (view, M2). */
const ImportExportMenu = ({ countries, defaultCountry, canManage, onImport, triggerRef }: ImportExportMenuProps) => {
  const { t } = useTranslation();
  const { download: save, preparing } = useWorkbookDownload();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const known = (code?: string | null) => (countries.some((entry) => entry.code === code) ? code : undefined);
  const country = known(picked) ?? known(defaultCountry) ?? countries[0]?.code;

  const download = async (prefill: boolean) => {
    if (!country || preparing) return;
    await save(country, prefill);
    // M14: back on the menu button, unless the user has moved on meanwhile
    const active = document.activeElement;
    if (!active || active === document.body || active === triggerRef.current) triggerRef.current?.focus();
  };

  const item = 'items-start gap-2.5 py-[7px] text-[13px] [&>svg]:mt-0.5 [&>svg]:text-muted-foreground';

  return (
    <DropdownMenu open={open} onOpenChange={(next) => setOpen(next && !preparing)}>
      <DropdownMenuTrigger asChild>
        {/* aria-disabled, not disabled: the button keeps focus while the file is prepared (M14). */}
        <Button ref={triggerRef} variant="outline" aria-disabled={preparing} aria-busy={preparing} className="gap-2">
          {preparing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
          {preparing ? t('Preparing the file…') : t('Import / Export')}
          {!preparing && <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[300px] p-1.5">
        <DropdownMenuLabel className="text-[13px] font-medium">{t('Country')}</DropdownMenuLabel>
        {countries.length === 0 ? (
          <p className="px-2 pb-1.5 text-xs text-muted-foreground">{t('No country in the referential yet')}</p>
        ) : (
          <DropdownMenuRadioGroup value={country} onValueChange={setPicked}>
            {countries.map((entry) => (
              // Picking a country keeps the menu open: it scopes the actions below.
              <DropdownMenuRadioItem key={entry.code} value={entry.code} onSelect={(event) => event.preventDefault()} className="text-[13px]">
                {entry.name}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        )}
        <DropdownMenuSeparator />
        {canManage && (
          <DropdownMenuItem className={item} disabled={!country} onSelect={() => download(false)}>
            <FileSpreadsheet aria-hidden="true" />
            <span className="flex flex-col gap-px">
              <span className="font-medium">{t('Download the template')}</span>
              <span className="text-xs text-muted-foreground">{t('Empty file to fill in')}</span>
            </span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className={item} disabled={!country} onSelect={() => download(true)}>
          <Download aria-hidden="true" />
          <span className="flex flex-col gap-px">
            <span className="font-medium">{t('Export the database')}</span>
            <span className="text-xs text-muted-foreground">{t('Every technician of the country, re-importable')}</span>
          </span>
        </DropdownMenuItem>
        {canManage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-[13px] font-medium [&>svg]:text-muted-foreground" disabled={!country} onSelect={() => country && onImport(country)}>
              <Upload aria-hidden="true" />
              {t('Import a file')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ImportExportMenu;
